import { Action, ThunkAction, URLParameters, VerifyEmailOtpError } from '../types';
import * as queryString from 'query-string';
import { addFlag } from './flag-actions';
import { defineMessages } from 'react-intl';
import { cobrandingURLParams } from '../utilities/applications';
import { AnalyticsClient } from '../utilities/analytics/analytics-web-client';
import { SignupVerifyEmailOtpPageState } from '../pages/signup/verify-email/otp';
import { captureTaggedException } from '../utilities/analytics/error-reporting';

export interface VerifyEmailOtpPayload {
  otpCode: string;
  token?: string;
}

export interface ResendEmailOtpPayload {
  token: string;
}

export const verifyEmailOtpSuccess = (redirectTo: string): Action => ({
  type: 'VERIFY_EMAIL_OTP_SUCCESS',
  redirectTo,
});

export const verifyEmailOtpFailure = (error: VerifyEmailOtpError): Action => ({
  type: 'VERIFY_EMAIL_OTP_FAILURE',
  error,
});

export const verifyEmailOtpRequest = (): Action => ({
  type: 'VERIFY_EMAIL_OTP_REQUEST',
});

export const verifyEmailOtpSetState = (state: SignupVerifyEmailOtpPageState): Action => ({
  type: 'VERIFY_EMAIL_OTP_SET_STATE',
  state,
});

export const verifyEmailOtp =
  (otp: string, pageId: string, analyticsClient: AnalyticsClient): ThunkAction =>
  async (dispatch, getState) => {
    const {
      csrfToken,
      cobranding: { productHint },
      verifyEmailOtp: { redirectType },
    } = getState();
    const { token }: URLParameters = queryString.parse(window.location.search);
    const params = cobrandingURLParams(window.location, productHint);

    const payload: VerifyEmailOtpPayload = {
      otpCode: otp,
      token: token,
    };

    dispatch(verifyEmailOtpRequest());

    return fetch('/rest/signup/verify-email/otp', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
      .then(response =>
        response.json().then(({ error, token }) => {
          if (token) {
            analyticsClient.trackingEvent({
              page: pageId,
              action: 'submitted',
              subject: 'otp_submission',
              attributes: { result: 'success', signupType: redirectType },
            });

            const redirectToUrl = new URLSearchParams({
              token,
              ...params,
            });

            const redirectTo = `/signup/welcome?${redirectToUrl.toString()}`;

            dispatch(verifyEmailOtpSuccess(redirectTo));
          } else {
            const result = error || 'internal_server_error';
            dispatch(verifyEmailOtpFailure(result));
            captureTaggedException(error, {
              capabilities: ['capability-group--signup', 'capability-name--verify-email'],
              tags: { otp: 'otp_submission' },
            });
            analyticsClient.trackingEvent({
              page: pageId,
              action: 'submitted',
              subject: 'otp_submission',
              attributes: { result, signupType: redirectType },
            });
          }
        })
      )
      .catch(err => {
        dispatch(verifyEmailOtpFailure('unexpected_error'));
        captureTaggedException(err, {
          capabilities: ['capability-group--signup', 'capability-name--verify-email'],
          tags: { otp: 'otp_submission' },
        });
        analyticsClient.trackingEvent({
          page: pageId,
          action: 'submitted',
          subject: 'otp_submission',
          attributes: { result: 'unexpected_error', signupType: redirectType },
        });
      });
  };

export const resendEmailOtp =
  (
    pageId: string,
    analyticsClient: AnalyticsClient,
    navigate: (url: string) => void
  ): ThunkAction =>
  async (dispatch, getState) => {
    const {
      csrfToken,
      cobranding: { productHint },
      verifyEmailOtp: { redirectType },
    } = getState();
    const { token }: URLParameters = queryString.parse(window.location.search);

    const params = cobrandingURLParams(window.location, productHint);

    type LoginRedirectError = 'verify.email.error' | 'verify.email.expired';

    const loginRedirectWithError = (error: LoginRedirectError) => {
      // @ts-expect-error URLSearchParams types aren't up to date
      const url = new URLSearchParams({
        ...params,
        errorCode: error,
        prompt: true,
      });
      return url.toString();
    };

    const payload: ResendEmailOtpPayload = {
      token: token as string,
    };

    return fetch('/rest/signup/verify-email/otp/resend', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    }).then(response =>
      response
        .json()
        .then(({ error, token }) => {
          if (response.status === 200) {
            const urlWithNewToken = new URLSearchParams({
              token,
              ...params,
            });
            analyticsClient.trackingEvent({
              page: pageId,
              action: 'link clicked',
              subject: 'resend_otp',
              attributes: { result: 'success', signupType: redirectType },
            });
            dispatch(
              addFlag(
                'success',
                resendOtpMessages.resendEmailOtpSuccessTitle,
                resendOtpMessages.resendEmailOtpSuccessBody
              )
            );
            return window.history.replaceState(
              window.history.state,
              '',
              `/signup/verify-email/otp?${urlWithNewToken.toString()}`
            );
          }
          if (response.status === 400) {
            analyticsClient.trackingEvent({
              page: pageId,
              action: 'link clicked',
              subject: 'resend_otp',
              attributes: { result: error, signupType: redirectType },
            });
            captureTaggedException(error, {
              capabilities: ['capability-group--signup', 'capability-name--verify-email'],
              tags: { otp: 'resend_otp' },
            });
            if (error === 'expired_token') {
              return navigate(`/login?${loginRedirectWithError('verify.email.expired')}`);
            } else {
              return navigate(`/login?${loginRedirectWithError('verify.email.error')}`);
            }
          }

          if (response.status === 429) {
            analyticsClient.trackingEvent({
              page: pageId,
              action: 'link clicked',
              subject: 'resend_otp',
              attributes: { result: error, signupType: redirectType },
            });
            return dispatch(
              addFlag(
                'error',
                resendOtpMessages.emailSentFailFlagTitle,
                resendOtpMessages.emailSentFailFlagBody
              )
            );
          }
        })
        .catch(err => {
          analyticsClient.trackingEvent({
            page: pageId,
            action: 'link clicked',
            subject: 'resend_otp',
            attributes: { result: 'unexpected_error', signupType: redirectType },
          });
          captureTaggedException(err, {
            capabilities: ['capability-group--signup', 'capability-name--verify-email'],
            tags: { otp: 'resend_otp' },
          });
          return navigate(`/login?${loginRedirectWithError('verify.email.error')}`);
        })
    );
  };

export const resendOtpMessages = defineMessages({
  emailSentFailFlagTitle: {
    id: 'signup.verify-email.otp.resend.rate.limit.exceeded.title',
    description:
      'Title of flag to show that the request for a new one time code failed due to a rate limit error',
    defaultMessage: 'Too many attempts.',
  },
  emailSentFailFlagBody: {
    id: 'signup.verify-email.otp.resend.rate.limit.exceeded.body',
    description:
      'Body of flag to show that the request for a new one time code failed due to a rate limit error',
    defaultMessage: 'Try again later.',
  },
  resendEmailOtpSuccessTitle: {
    id: 'signup.verify-email.otp.resend.success.title',
    description: 'Title of flag to show that the request for a new one time code was successful',
    defaultMessage: 'Email sent with new code',
  },
  resendEmailOtpSuccessBody: {
    id: 'signup.verify-email.otp.resend.success.body',
    description: 'Body of flag to show that the request for a new one time code was successful',
    defaultMessage: 'Please check your inbox.',
  },
});
